import { Empty, Table } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PageDeleteModal from "./PageDeleteModal";
import UIModulePages from "./pages/Page";
import RoleItem from "./pages/PageRoleItem";
import PageComponentRoleCheckBox from "./pages/PageComponentRoleCheckBox";
import UIModulePage from "./pages/ModulePage";
import PageUiComponentRoleCheckBox from "./pages/UiComponentRoleCheckBox";
import toast from "react-hot-toast";
import {
  AppDispatch,
  deleteSystemUiModule,
  deleteSystemUiModuleClear,
  deleteSystemUiModuleComponent,
  deleteSystemUiModuleComponentClear,
  deleteSystemUiModuleComponentRequest,
  deleteSystemUiModuleRequest,
  getSystemRoleList,
  getSystemRoleListRequest,
  getSystemUiModuleList,
  getSystemUiModuleListRequest,
} from "../../../../redux";

interface PageListProps {
  setOpenModal: (open: boolean) => void;
  setActionType: (type: string) => void;
  setFiledId: (id: string) => void;
  setActionItem: (item: any) => void;
  getId: string;
  setLinkOpenModal: (open: boolean) => void;
  setExpandedRowKey: (key: string | null) => void;
  expandedRowKey: string | null;
  setLinkType: (type: string) => void;
  setOpenRoleModal: any;
  searchTerm?: string;
}

interface RecordType {
  id?: string;
  name?: string;
  type?: string;
  children?: RecordType[];
  uiModuleComponentList?: RecordType[];
}

interface Role {
  id?: string;
  name?: string;
}

const PageList: React.FC<PageListProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();

  const { result: systemRoleListItems, loading: systemRoleListLoading } =
    useSelector(getSystemRoleList);
  const { result: pageList } = useSelector(getSystemUiModuleList);

  const {
    result: deleteResult,
    loading: deleteLoading,
    error: deleteError,
  } = useSelector(deleteSystemUiModule);

  const {
    result: deletePageResult,
    loading: deletePageLoading,
    error: deletePageError,
  } = useSelector(deleteSystemUiModuleComponent);

  const {
    setOpenModal,
    setActionType,
    setFiledId,
    setActionItem,
    getId,
    setLinkOpenModal,
    setExpandedRowKey,
    expandedRowKey,
    setLinkType,
    setOpenRoleModal,
    searchTerm = "",
  } = props;

  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deleteName, setDeleteName] = useState<string>("");
  const [deleteId, setDeleteId] = useState<string>("");
  const [actionDelete, setActionDelete] = useState<string | null>(null);
  const [pageItems, setPageItems] = useState<RecordType[]>([]);

  const handleAdd = (record: RecordType) => {
    setOpenModal(true);
    setActionType("add_ui_component");
    setFiledId(record?.id || "");
    setActionItem({});
  };

  const handleLink = (record: RecordType) => {
    setLinkOpenModal(true);
    setLinkType("link_component");
    setActionType("");
    setFiledId(record?.id || "");
    setActionItem(record);
  };

  const handleEdit = (record: RecordType) => {
    setOpenModal(true);
    setActionType("edit_page");
    setFiledId(record?.id || "");
    setActionItem(record);
  };

  const handleEditComponent = (record: RecordType) => {
    setOpenModal(true);
    setActionType("edit_page_component");
    setFiledId(record?.id || "");
    setLinkType("");
    setActionItem(record);
  };

  const handleEditRole = (record: any) => {
    setOpenRoleModal(true);
    setActionType("edit_role");
    setFiledId(record?.id || "");
    setActionItem(record);
  };

  const handleDelete = (record: RecordType) => {
    setDeleteModalOpen(true);
    setActionDelete("delete_page");
    setDeleteName(record?.name || "");
    setDeleteId(record?.id || "");
  };

  const handleDeleteComponent = (record: RecordType) => {
    setDeleteModalOpen(true);
    setActionDelete("delete_page_component");
    setDeleteName(record?.name || "");
    setDeleteId(record?.id || "");
  };

  const handleDeleteClose = () => {
    setDeleteModalOpen(false);
  };

  const deleteOneSystemUiModule = (id: string) => {
    dispatch(deleteSystemUiModuleRequest({ id }));
  };

  const deleteOneSystemUiModuleComponent = (id: string) => {
    dispatch(deleteSystemUiModuleComponentRequest({ id }));
  };

  useEffect(() => {
    if (deleteResult?.status === "success") {
      toast.success("Page deleted successfully");
      systemUiModuleList();
      handleDeleteClose();
      dispatch(deleteSystemUiModuleClear());
    } else if (deleteError?.message === "error") {
      toast.error(deleteError.message);
      systemUiModuleList();
      dispatch(deleteSystemUiModuleClear());
    } else if (deletePageResult?.status === "success") {
      toast.success("Page component deleted successfully");
      systemUiModuleList();
      handleDeleteClose();
      setExpandedRowKey(null);
      dispatch(deleteSystemUiModuleComponentClear());
    } else if (deletePageError?.message === "error") {
      toast.error(deletePageError.message);
      systemUiModuleList();
      setExpandedRowKey(null);
      dispatch(deleteSystemUiModuleComponentClear());
    }
  }, [deleteResult, deleteError, deletePageResult, deletePageError]);

  const onDelete = () => {
    if (actionDelete === "delete_page") {
      deleteOneSystemUiModule(deleteId);
    } else {
      deleteOneSystemUiModuleComponent(deleteId);
    }
  };

  const systemRoleList = () => {
    dispatch(
      getSystemRoleListRequest({
        sort: [{ field: "name", order: "asc" }],
      })
    );
  };

  const systemUiModuleList = () => {
    dispatch(
      getSystemUiModuleListRequest({
        sort: [{ field: "name", order: "asc" }],
      })
    );
  };

  useEffect(() => {
    systemRoleList();
    systemUiModuleList();
  }, []);

  useEffect(() => {
    if (pageList?.items) {
      setPageItems(pageList?.items || []);
    }
  }, [pageList]);

  const handleExpand = (record: RecordType) => {
    if (expandedRowKey === record?.id) {
      setExpandedRowKey(null);
    } else {
      setExpandedRowKey(record?.id || "");
    }
  };

  const filteredPageItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return pageItems;
    return pageItems
      .map((page) => {
        const pageMatches = page.name?.toLowerCase().includes(term);
        const matchingChildren = (page.uiModuleComponentList || []).filter(
          (c) => c.name?.toLowerCase().includes(term)
        );
        if (pageMatches) {
          return page;
        }
        if (matchingChildren.length) {
          return { ...page, uiModuleComponentList: matchingChildren };
        }
        return null;
      })
      .filter(Boolean) as RecordType[];
  }, [pageItems, searchTerm]);

  const dataSource = useMemo(
    () =>
      filteredPageItems.map((item) => ({
        ...item,
        key: item.id,
        type: "page",
        children: item.uiModuleComponentList,
      })),
    [filteredPageItems]
  );

  const roleItems: Role[] = systemRoleListItems?.items || [];

  const columns: any[] = useMemo(() => {
    const base: any[] = [
      {
        title: "",
        dataIndex: "",
        key: "expand-spacer",
        fixed: "left",
        width: 36,
      },
      {
        title: "Pages",
        dataIndex: "name",
        key: "pages",
        fixed: "left",
        width: 240,
        render: (_text: any, record: RecordType, pageIndex: number) => {
          if (record?.type === "page") {
            return (
              <UIModulePages
                key={record.id || pageIndex}
                handleAdd={handleAdd}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                setIsHovered={setIsHovered}
                expandedRowKey={expandedRowKey}
                record={record as any}
              />
            );
          }
          return (
            <UIModulePage
              key={record.id || pageIndex}
              handleLink={handleLink}
              handleEdit={handleEditComponent}
              handleDelete={handleDeleteComponent}
              setIsHovered={setIsHovered}
              isHovered={isHovered}
              expandedRowKey={expandedRowKey || ""}
              record={record as any}
            />
          );
        },
      },
    ];

    const roleColumns = roleItems.map((role, roleIndex) => ({
      title: (
        <RoleItem
          key={role.id || roleIndex}
          item={role}
          index={roleIndex}
          handleEditRole={handleEditRole}
        />
      ),
      dataIndex: role.id,
      key: `role_${role.id || roleIndex}`,
      width: 120,
      align: "center" as const,
      render: (_text: any, record: RecordType, index: number) => {
        if (record?.type === "page") {
          return (
            <PageComponentRoleCheckBox
              pageComponent={record}
              role={role || ""}
              key={`${record.id}_${role.id}`}
            />
          );
        }
        return (
          <PageUiComponentRoleCheckBox
            pageComponent={record}
            role={role}
            index={index}
            key={`${record.id}_${role.id}`}
          />
        );
      },
    }));

    return [...base, ...roleColumns];
  }, [roleItems, expandedRowKey, isHovered]);

  const rowClassName = (record: RecordType, _index: number) => {
    if (record?.children) {
      const myCategoryIndex = filteredPageItems.findIndex(
        (x) => x.id === record?.id
      );
      return myCategoryIndex % 2 === 0 ? "page-even-row" : "page-odd-row";
    }
    const parent = filteredPageItems.find((x) =>
      x.uiModuleComponentList?.some((c) => c.id === record.id)
    );
    const childIndex =
      parent?.uiModuleComponentList?.findIndex((c) => c.id === record.id) ?? 0;
    return childIndex % 2 === 0
      ? "page-access-even-row"
      : "page-access-odd-row";
  };

  return (
    <div className="custom-table">
      <Table
        loading={systemRoleListLoading}
        sticky={{ offsetHeader: 0 }}
        dataSource={dataSource}
        onExpand={(_expanded, record) => handleExpand(record)}
        pagination={false}
        className="table-striped-rows admin-table"
        scroll={{ x: "max-content" }}
        columns={columns}
        rowClassName={(record, index) => rowClassName(record, index)}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                searchTerm ? "No pages match your search" : "No pages found"
              }
            />
          ),
        }}
      />

      <PageDeleteModal
        deleteModalOpen={deleteModalOpen}
        handleDeleteClose={handleDeleteClose}
        deleteName={deleteName}
        onDelete={onDelete}
        deleteLoading={deleteLoading}
        deletePageLoading={deletePageLoading}
        actionDelete={actionDelete || ""}
      />
    </div>
  );
};

export default PageList;
